import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Empty } from "./ui";
import { errMsg } from "@/lib/api";

const CategoryList = () => {
  const { api, me, categories, reloadCategories } = useOutletContext();
  const [name, setName] = useState("");
  const isAdmin = me.role === "admin";
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/categories", { name }); setName(""); reloadCategories(); toast.success("Categoria criada."); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); reloadCategories(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold">Categorias</h2>
      <p className="text-sm text-muted-foreground">Cada participação é classificada automaticamente nos bastidores (regras próprias e IA nos casos de dúvida). Uma mensagem pode ter várias categorias e a equipe pode corrigir na conversa. Categorias do sistema não podem ser excluídas.</p>
      <ul className="divide-y divide-zap-line rounded-lg border border-zap-line bg-white">
        {categories.map((c) => (
          <li key={c.id} data-testid={`category-${c.id}`} className="flex items-center justify-between px-4 py-2.5 text-[15px]">
            {c.name}{c.system && <span className="ml-2 text-xs text-muted-foreground">sistema</span>}
            {isAdmin && !c.system && <button data-testid={`category-delete-${c.id}`} onClick={() => del(c.id)} aria-label="Excluir" className="rounded p-2 text-muted-foreground hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
      </ul>
      {isAdmin && (
        <form onSubmit={add} className="flex gap-2">
          <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nova categoria" maxLength={60} />
          <Button data-testid="category-add" disabled={name.trim().length < 2}><Plus className="mr-1 h-4 w-4" /> Adicionar</Button>
        </form>
      )}
    </section>
  );
};

const QuickReplies = () => {
  const { api } = useOutletContext();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ title: "", text: "" });
  const load = useCallback(() => api.get("/admin/quick-replies").then((r) => setItems(r.data)).catch(() => setItems([])), [api]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/quick-replies", f); setF({ title: "", text: "" }); load(); toast.success("Resposta salva."); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/quick-replies/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold">Respostas prontas</h2>
      <p className="text-sm text-muted-foreground">Aparecem acima do campo de resposta. Use {"{nome}"} para inserir o primeiro nome do participante.</p>
      {items === null ? null : items.length === 0 ? <Empty testId="quick-empty">Nenhuma resposta pronta cadastrada.</Empty> : (
        <ul className="divide-y divide-zap-line rounded-lg border border-zap-line bg-white">
          {items.map((q) => (
            <li key={q.id} data-testid={`quick-item-${q.id}`} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1"><p className="font-semibold">{q.title}</p><p className="whitespace-pre-wrap text-sm text-muted-foreground">{q.text}</p></div>
              <button data-testid={`quick-delete-${q.id}`} onClick={() => del(q.id)} aria-label="Excluir" className="rounded p-2 text-muted-foreground hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="space-y-2 rounded-lg border border-zap-line bg-white p-4">
        <Input data-testid="quick-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Título (ex.: Oração recebida)" maxLength={60} />
        <Textarea data-testid="quick-text-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={3} placeholder="Texto da resposta" />
        <Button data-testid="quick-add" disabled={f.title.trim().length < 2 || f.text.trim().length < 2}>Salvar resposta</Button>
      </form>
    </section>
  );
};

export default function Categories() {
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Categorias e respostas" description="Organização das participações e respostas da equipe." />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="grid max-w-5xl gap-10 px-5 py-6 md:px-8 lg:grid-cols-2"><CategoryList /><p className="text-sm text-muted-foreground">As respostas prontas e os áudios oficiais ficam em <Link to="/admin/respostas" className="font-semibold text-zap-green hover:underline">Biblioteca de respostas</Link>.</p></div>
      </div>
    </div>
  );
}
